import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import { useSubscriptionPlans } from "../../hooks/useSubscriptions";
import { useAuth } from "../../hooks/useAuth";
import PageShell, { PageHero, Steps } from "./PageShell";
import PricingV2 from "../components/PricingV2";
import DoorsV2 from "../components/DoorsV2";
import { Arrow, Display, Eyebrow } from "../components/Primitives";
import { pickLang } from "../copy";
import { route } from "../paths";

const fmt = (n: number | string, fr: boolean) => {
  const v = Number(n) || 0;
  const s = v % 1 === 0 ? String(v) : v.toFixed(2);
  return fr ? s.replace(".", ",") : s;
};

const PricingPageV2: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const lang = pickLang(currentLanguage);
  const isFr = lang === "fr";
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { plans } = useSubscriptionPlans({ autoFetch: true });

  const options = isFr
    ? [
        { title: "Collecte & livraison", desc: "On vient chercher vos paires chez vous et on les ramène, à l'horaire convenu.", meta: "Option · dès 15€ par commande" },
        { title: "Express 24h ou 48h", desc: "Priorité atelier et retour sous 24h ou 48h, au choix.", meta: "Option · supplément selon le délai" },
        { title: "Imperméabilisation", desc: "Traitement protecteur appliqué après nettoyage, pluie et taches glissent.", meta: "Option · dès 2€ par paire" },
        { title: "Désodorisation renforcée", desc: "Traitement intérieur approfondi pour les paires très portées.", meta: "Option · par paire" },
      ]
    : [
        { title: "Pickup & delivery", desc: "We collect your pairs at home and bring them back at the agreed time.", meta: "Add-on · from €15 per order" },
        { title: "Express 24h or 48h", desc: "Workshop priority and return within 24h or 48h, your choice.", meta: "Add-on · supplement depends on turnaround" },
        { title: "Waterproofing", desc: "Protective treatment applied after cleaning, rain and stains slide off.", meta: "Add-on · from €2 per pair" },
        { title: "Deep deodorizing", desc: "In-depth interior treatment for heavily worn pairs.", meta: "Add-on · per pair" },
      ];

  const activePlans = [...plans]
    .filter((p) => p.isActive !== false)
    .sort((a, b) => (a.displayOrder ?? a.sortOrder ?? 0) - (b.displayOrder ?? b.sortOrder ?? 0));

  const subscribe = (planId: string) =>
    navigate(
      isAuthenticated
        ? `/${lang}/dashboard?tab=subscription&plan=${planId}`
        : `/${lang}/login?redirect=dashboard&tab=subscription&plan=${planId}`,
    );

  return (
    <PageShell
      seo={{
        title: isFr
          ? "Tarifs nettoyage de baskets : dès 20€ la paire | Lave Tes Baskets"
          : "Sneaker cleaning prices: from €20 per pair | Lave Tes Baskets",
        description: isFr
          ? "Prix par paire, sans surprise : Standard, Luxe, Enfant. Options à la carte : collecte & livraison dès 15€, express 24h ou 48h, imperméabilisation dès 2€. Prix affiché avant paiement."
          : "Per-pair prices, no surprises: Standard, Luxury, Kids. À la carte add-ons: pickup & delivery from €15, 24h or 48h express, waterproofing from €2. Price shown before payment.",
        canonicalPath: route(lang, "tarifs"),
      }}
    >
      <PageHero
        crumbs={[
          { label: isFr ? "Accueil" : "Home", to: route(lang) },
          { label: isFr ? "Tarifs" : "Pricing" },
        ]}
        index="02"
        eyebrow={isFr ? "Tarifs" : "Pricing"}
        title={
          isFr ? (
            <>
              Simple. <em className="italic text-tangerine">Par paire.</em>
            </>
          ) : (
            <>
              Simple. <em className="italic text-tangerine">Per pair.</em>
            </>
          )
        }
        lede={
          isFr
            ? "Une base, vos options, aucune surprise : le total se met à jour pendant que vous composez et s'affiche avant paiement."
            : "One base, your add-ons, no surprises: the total updates while you build and shows before payment."
        }
        actions={
          <>
            <Link to={route(lang, "sur-mesure")} className="v2-btn v2-btn-solid">
              {isFr ? "Composer ma prestation" : "Build my service"}
              <Arrow />
            </Link>
            <Link to={route(lang, "devis")} className="v2-btn v2-btn-ghost">
              {isFr ? "Devis photo gratuit" : "Free photo estimate"}
            </Link>
          </>
        }
      />

      <PricingV2 hideHeader />

      {/* add-ons */}
      <section className="bg-bone py-20 text-ink sm:py-28">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <Eyebrow index="03">{isFr ? "Les options" : "Add-ons"}</Eyebrow>
          <Display className="mt-5 max-w-3xl text-[clamp(2.2rem,5.5vw,5rem)]">
            {isFr ? "Ajoutez ce dont vous avez besoin. Rien de plus." : "Add what you need. Nothing more."}
          </Display>
          <div className="mt-12">
            <Steps steps={options} />
          </div>
          <p className="v2-reveal mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">
            {isFr
              ? "Le détail des options et leurs prix s'affiche dans le configurateur avant paiement."
              : "Add-on details and prices show in the configurator before payment."}
          </p>
        </div>
      </section>

      {/* subscriptions — only when the API exposes plans */}
      {activePlans.length > 0 && (
        <section className="v2-hair border-t bg-ink py-20 text-bone sm:py-28">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
            <Eyebrow index="04">{t("pricing.toggle.subscriptions")}</Eyebrow>
            <Display className="mt-5 max-w-3xl text-[clamp(2.2rem,5.5vw,5rem)]">
              {isFr ? "Pour les paires qui vivent." : "For pairs that get worn."}
            </Display>
            <p className="v2-reveal mt-5 max-w-lg text-bone/60" data-delay="2">
              {t("pricing.subscriptions.noEngagement")}
            </p>
            <div className="mt-12 grid gap-px border border-bone/10 bg-bone/10 md:grid-cols-2 lg:grid-cols-3">
              {activePlans.map((p, i) => (
                <article key={p.id} className="v2-reveal flex flex-col bg-ink p-7 sm:p-9" data-delay={String((i % 3) + 1)}>
                  {p.service?.name && (
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-teal-light/80">{p.service.name}</div>
                  )}
                  <h3 className="mt-4 font-display text-3xl leading-tight">{p.name}</h3>
                  <div className="mt-5 flex items-baseline gap-3">
                    <span className="font-display text-6xl leading-none">
                      {fmt(p.monthlyPrice, isFr)}
                      <span className="text-4xl">€</span>
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/50">
                      {t("pricing.subscriptions.monthly")}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-bone/60">{p.description}</p>
                  <div className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-bone/70">
                    {`${p.creditsPerMonth ?? p.creditsPerCycle ?? 0} ${t("pricing.subscriptions.cleaningsPerMonth")}`}
                  </div>
                  <ul className="mt-5 space-y-2 text-sm">
                    {(p.service?.features ?? p.features ?? []).map((f, k) => (
                      <li key={k} className="flex gap-3">
                        <span className="mt-[9px] h-px w-3 flex-shrink-0 bg-teal" />
                        <span className="text-bone/85">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => subscribe(p.id)} className="v2-btn v2-btn-solid mt-auto w-full justify-center pt-4">
                    {t("pricing.subscriptions.subscribe")}
                    <Arrow />
                  </button>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-ink py-16 text-bone sm:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <DoorsV2 />
        </div>
      </section>
    </PageShell>
  );
};

export default PricingPageV2;
